import Brand from "@/Components/Base/Brand";
import { CSSProperties, forwardRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AllergenPosterBranch } from "../AllergenPoster/AllergenPoster";
import { PosterColors, withAlpha } from "../AllergenPoster/allergenPosterUtils";
import {
  formatDatePart,
  formatHours,
  formatTime,
  getRosterPageSize,
  getWeekPageBody,
  getWeekRowHeight,
  RosterOrientation,
  RosterSection,
  WEEK_ROW_BASE_HEIGHT,
  WEEK_SHIFT_LINE_HEIGHT,
  WEEK_STAFFING_HEIGHT,
  WeekRosterRow,
} from "./scheduleRosterUtils";

const TEXT_COLOR = "#1f2933";
const MUTED_COLOR = "#6b7280";
const BORDER_COLOR = "#e5e7eb";
const WEEKEND_TINT = "#f8fafc";
const INACTIVE_TINT = "#eef0f3";
const FONT_FAMILY =
  "'Inter', 'Helvetica Neue', Helvetica, Arial, 'Segoe UI', sans-serif";

const WEEK_PADDING_X = 32;
const WEEK_NAME_WIDTH: Record<RosterOrientation, number> = {
  portrait: 140,
  landscape: 200,
};
const WEEK_TOTAL_WIDTH = 56;
const WEEK_PILL_HEIGHT = 18;


type RosterShared = {
  orientation: RosterOrientation;
  colors: PosterColors;
  title: string;
  period: string;
  summary: string;
  branch: AllergenPosterBranch;
  logoUrl?: string | null;
  generatedAt: string;
  workerColors: Record<string, string>;
  language: string;
  showStaffing: boolean;
  showBlankLines: boolean;
  hideInactiveDays?: boolean;
};

type HeaderProps = Pick<
  RosterShared,
  "colors" | "title" | "period" | "summary" | "branch" | "logoUrl"
> & { compact?: boolean; paddingX: number };

const RosterHeader = ({
  colors,
  title,
  period,
  summary,
  branch,
  logoUrl,
  compact,
  paddingX,
}: HeaderProps) => (
  <div
    style={{
      background: colors.primary,
      color: colors.primaryText,
      padding: compact ? `16px ${paddingX}px` : `26px ${paddingX}px 22px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 24,
      boxSizing: "border-box",
      flexShrink: 0,
    }}
  >
    <div style={{ minWidth: 0, flex: 1 }}>
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: 2.5,
          textTransform: "uppercase",
          opacity: 0.85,
          fontWeight: 600,
        }}
      >
        {branch.name}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginTop: compact ? 4 : 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: compact ? 26 : 32,
            lineHeight: 1.1,
            fontWeight: 800,
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: compact ? 15 : 17, fontWeight: 600 }}>
          {period}
        </span>
      </div>
      <div style={{ fontSize: 11, marginTop: compact ? 4 : 10, opacity: 0.85 }}>
        {[summary, branch.address, branch.phone].filter(Boolean).join("  ·  ")}
      </div>
    </div>
    <div
      style={{
        background: "#ffffff",
        borderRadius: 14,
        padding: 12,
        width: compact ? 128 : 150,
        height: compact ? 64 : 90,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          crossOrigin="anonymous"
          alt={branch.name}
          style={{
            maxWidth: compact ? 104 : 126,
            maxHeight: compact ? 40 : 66,
            width: "auto",
            height: "auto",
            objectFit: "contain",
          }}
        />
      ) : (
        <Brand style={{ width: compact ? 100 : 122, height: 26 }} />
      )}
    </div>
  </div>
);

const RosterFooter = ({
  generatedAt,
  pageIndex,
  pageCount,
  paddingX,
}: {
  generatedAt: string;
  pageIndex: number;
  pageCount: number;
  paddingX: number;
}) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        margin: `0 ${paddingX}px`,
        padding: "10px 0 18px",
        borderTop: `1px solid ${BORDER_COLOR}`,
        display: "flex",
        justifyContent: "space-between",
        fontSize: 8,
        color: MUTED_COLOR,
        flexShrink: 0,
      }}
    >
      <span>{t("scheduleRoster.generatedAt", { date: generatedAt })}</span>
      <span>
        {t("scheduleRoster.page", { page: pageIndex + 1, total: pageCount })}
      </span>
    </div>
  );
};

const pageStyle = (orientation: RosterOrientation): CSSProperties => {
  const { width, height } = getRosterPageSize(orientation);
  return {
    width,
    height,
    background: "#ffffff",
    color: TEXT_COLOR,
    fontFamily: FONT_FAMILY,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    boxSizing: "border-box",
  };
};

const WorkerDot = ({ color, size = 8 }: { color: string; size?: number }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
      display: "inline-block",
    }}
  />
);

const isWeekend = (index: number) => index >= 5;

type WeekRosterPageProps = RosterShared & {
  rows: WeekRosterRow[];
  section: RosterSection;
  sectionPageIndex: number;
  isSectionEnd: boolean;
  showTotals: boolean;
  pageIndex: number;
  pageCount: number;
};

export const WeekRosterPage = forwardRef<HTMLDivElement, WeekRosterPageProps>(
  (
    {
      rows,
      section,
      sectionPageIndex,
      isSectionEnd,
      showTotals,
      pageIndex,
      pageCount,
      workerColors,
      language,
      showStaffing,
      showBlankLines,
      hideInactiveDays,
      ...shared
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { colors } = shared;
    const { days, activeDays, dayCounts, totalMinutes } = section;
    // Day columns to draw; days outside the month can be left out entirely
    const dayIndexes = days
      .map((_, index) => index)
      .filter((index) => !hideInactiveDays || activeDays[index]);
    const nameWidth = WEEK_NAME_WIDTH[shared.orientation];
    const hasStaffingRow =
      showStaffing && isSectionEnd && section.rows.length > 0;

    // Fill the rest of the page with empty rows to write in after printing
    const usedHeight = rows.reduce((sum, row) => sum + getWeekRowHeight(row), 0);
    const freeHeight =
      getWeekPageBody(shared.orientation, sectionPageIndex) +
      (hasStaffingRow ? 0 : WEEK_STAFFING_HEIGHT) -
      usedHeight;
    const blankRows = showBlankLines
      ? Math.max(0, Math.floor(freeHeight / WEEK_ROW_BASE_HEIGHT))
      : 0;

    const cell = (index: number, children: ReactNode, style?: CSSProperties) => (
      <div
        key={index}
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: WEEK_SHIFT_LINE_HEIGHT - WEEK_PILL_HEIGHT,
          padding: "0 3px",
          background: !activeDays[index]
            ? INACTIVE_TINT
            : isWeekend(index)
            ? WEEKEND_TINT
            : "transparent",
          borderLeft: `1px solid ${BORDER_COLOR}`,
          boxSizing: "border-box",
          ...style,
        }}
      >
        {children}
      </div>
    );

    return (
      <div ref={ref} style={pageStyle(shared.orientation)}>
        <RosterHeader
          {...shared}
          compact={sectionPageIndex > 0}
          paddingX={WEEK_PADDING_X}
        />

        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: `16px ${WEEK_PADDING_X}px 0`,
          }}
        >
          <div
            style={{
              display: "flex",
              height: 44,
              borderBottom: `2px solid ${colors.primary}`,
            }}
          >
            <div
              style={{
                width: nameWidth,
                flexShrink: 0,
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: 6,
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                color: MUTED_COLOR,
              }}
            >
              {t("scheduleRoster.employee")}
            </div>
            {dayIndexes.map((index) =>
              cell(
                index,
                <div
                  style={{
                    textAlign: "center",
                    paddingBottom: 4,
                    opacity: activeDays[index] ? 1 : 0.35,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      lineHeight: "12px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      color: colors.primary,
                    }}
                  >
                    {formatDatePart(days[index], language, { weekday: "short" })}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: "15px",
                      fontWeight: 800,
                      marginTop: 2,
                    }}
                  >
                    {formatDatePart(days[index], language, {
                      day: "numeric",
                      month: "short",
                    })}
                  </div>
                </div>,
                {
                  justifyContent: "flex-end",
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }
              )
            )}
            {showTotals && (
              <div
                style={{
                  width: WEEK_TOTAL_WIDTH,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                  paddingBottom: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  color: MUTED_COLOR,
                }}
              >
                {t("scheduleRoster.total")}
              </div>
            )}
          </div>

          {rows.map((row, rowIndex) => {
            const color = workerColors[row.workerKey];
            return (
              <div
                key={row.workerKey}
                style={{
                  display: "flex",
                  height: getWeekRowHeight(row),
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                  background:
                    rowIndex % 2 === 1 ? withAlpha(colors.primary, 0.03) : "",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    width: nameWidth,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    paddingRight: 6,
                    fontSize: 11,
                    lineHeight: "13px",
                    fontWeight: 700,
                  }}
                >
                  <WorkerDot color={color} />
                  {/* Long names wrap to a second line instead of being cut off */}
                  <span
                    style={{
                      display: "block",
                      maxHeight: 26,
                      overflow: "hidden",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.name}
                  </span>
                </div>
                {dayIndexes.map((index) =>
                  cell(
                    index,
                    row.days[index].map((shift) => (
                      <div
                        key={shift.id}
                        style={{
                          height: WEEK_PILL_HEIGHT,
                          lineHeight: `${WEEK_PILL_HEIGHT - 2}px`,
                          borderRadius: 4,
                          background: withAlpha(color, 0.13),
                          border: `1px solid ${withAlpha(color, 0.45)}`,
                          textAlign: "center",
                          fontSize: 9.5,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                          color: TEXT_COLOR,
                          boxSizing: "border-box",
                        }}
                      >
                        {formatTime(shift.start)}–{formatTime(shift.end)}
                      </div>
                    ))
                  )
                )}
                {showTotals && (
                  <div
                    style={{
                      width: WEEK_TOTAL_WIDTH,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      fontSize: 10.5,
                      fontWeight: 700,
                      borderLeft: `1px solid ${BORDER_COLOR}`,
                    }}
                  >
                    <span style={{ display: "block", lineHeight: "14px" }}>
                      {t("scheduleRoster.hoursShort", {
                        value: formatHours(row.minutes, language),
                      })}
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {Array.from({ length: blankRows }, (_, blankIndex) => (
            <div
              key={`blank-${blankIndex}`}
              style={{
                display: "flex",
                height: WEEK_ROW_BASE_HEIGHT,
                borderBottom: `1px solid ${BORDER_COLOR}`,
                background:
                  (rows.length + blankIndex) % 2 === 1
                    ? withAlpha(colors.primary, 0.03)
                    : "",
                boxSizing: "border-box",
              }}
            >
              <div style={{ width: nameWidth, flexShrink: 0 }} />
              {dayIndexes.map((index) => cell(index, null))}
              {showTotals && (
                <div
                  style={{
                    width: WEEK_TOTAL_WIDTH,
                    flexShrink: 0,
                    borderLeft: `1px solid ${BORDER_COLOR}`,
                  }}
                />
              )}
            </div>
          ))}

          {hasStaffingRow && (
            <div
              style={{
                display: "flex",
                height: WEEK_STAFFING_HEIGHT - 4,
                marginTop: 4,
                borderRadius: 6,
                background: withAlpha(colors.primary, 0.08),
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              <div
                style={{
                  width: nameWidth,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 8,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  lineHeight: "14px",
                  color: colors.categoryText,
                  boxSizing: "border-box",
                }}
              >
                {t("scheduleRoster.staffed")}
              </div>
              {dayIndexes.map((index) =>
                cell(
                  index,
                  <span
                    style={{
                      display: "block",
                      lineHeight: "14px",
                      textAlign: "center",
                    }}
                  >
                    {activeDays[index]
                      ? t("scheduleRoster.people", { count: dayCounts[index] })
                      : "–"}
                  </span>,
                  { background: "transparent", borderLeft: "none" }
                )
              )}
              {showTotals && (
                <div
                  style={{
                    width: WEEK_TOTAL_WIDTH,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: 4,
                    lineHeight: "14px",
                    fontWeight: 800,
                    boxSizing: "border-box",
                  }}
                >
                  {t("scheduleRoster.hoursShort", {
                    value: formatHours(totalMinutes, language),
                  })}
                </div>
              )}
            </div>
          )}

          {!rows.length && !showBlankLines && (
            <div
              style={{
                padding: "48px 0",
                textAlign: "center",
                fontSize: 13,
                color: MUTED_COLOR,
              }}
            >
              {t("scheduleRoster.empty")}
            </div>
          )}
        </div>

        <RosterFooter
          generatedAt={shared.generatedAt}
          pageIndex={pageIndex}
          pageCount={pageCount}
          paddingX={WEEK_PADDING_X}
        />
      </div>
    );
  }
);

WeekRosterPage.displayName = "WeekRosterPage";
